// store/modules/choixBenec.js
import ChoixBenecAPI from "@/script/TransCam/ChoixBenec"; // adapte le chemin si besoin
import Assertion from "@/script/Assertion"; // adapte le chemin si besoin

export default {
    namespaced: true,

    state: () => ({
        choixBenec: null,   // valeur courante (short/int)
        loading: false,     // état de chargement pour UI
        error: null,        // dernier message/objet d'erreur
        donneeResponseADR: null,
        statutADR: "",      // statut de la derniere reponse ADR (ex. "Succes", "EnErreur")
        erreurADR: "",      // message complet d'erreur ADR (vide si pas d'erreur)
        // Resultat IMT (Interrogation Medecin Traitant / IMTi) : medecin traitant retourne + statut.
        // { statutExec, statutResult, code, message, nom, prenom, idAdeli, idRPPS, idPsam }
        medecinIMT: null,
    }),

    getters: {
        getChoixBenec: (state) => state.choixBenec,
        isLoading: (state) => state.loading,
        getError: (state) => state.error,
        getDonneeResponseADR : (state) => state.donneeResponseADR,
        getStatutADR: (state) => state.statutADR,
        getErreurADR: (state) => state.erreurADR,
        getMedecinIMT: (state) => state.medecinIMT,
    },

    mutations: {
        setChoixBenec(state, value) {
            state.choixBenec = value;
        },
        setLoading(state, value) {
            state.loading = !!value;
        },
        setError(state, err) {
            state.error = err || null;
        },
        setDonneeResponseADR(state, value) {
            state.donneeResponseADR = value;
        },
        setStatutADR(state, value) {
            state.statutADR = value || "";
        },
        setErreurADR(state, value) {
            state.erreurADR = value || "";
        },
        setMedecinIMT(state, value) {
            state.medecinIMT = value || null;
        },
    },

    actions: {
        // Récupère la valeur actuelle depuis l'API
        async fetchDonneeResponseADR({ commit }) {
            console.log("on fetchDonneeResponseADR ")
            try {
                const donnee = await Assertion.getDonneeResponseADR()
                console.log("donneeResponseADR = ", donnee);
                commit("setDonneeResponseADR", donnee);
            }
            catch (e) {
                throw e;
            }
        },
        // Recupere l'etat ADR complet (resultat + statut + erreur) pour le panneau ADR depliable.
        // Utilise par les 2 flux : Vitale (apres generation ADR) et degrade (apres creation patient).
        // Le backend serialise en PascalCase mais garde les cles telles quelles -> on gere les 2 casses.
        async fetchEtatADR({ commit }) {
            try {
                const donnee = await Assertion.getDonneeResponseADR();
                commit("setDonneeResponseADR", donnee);
                const etat = await Assertion.getStatutADR();
                commit("setStatutADR", etat?.adrStatut ?? etat?.AdrStatut ?? "");
                commit("setErreurADR", etat?.adrErreurTexte ?? etat?.AdrErreurTexte ?? "");
            } catch (e) {
                console.error("fetchEtatADR:", e);
            }
        },
        // Relit une fois le resultat IMT courant (medecin traitant + statut d'execution).
        // Backend PascalCase possible -> on gere les 2 casses. Retourne l'objet normalise.
        async fetchReponseIMT({ commit }) {
            try {
                const r = await Assertion.getReponseIMT();
                if (!r) { return null; }
                const med = {
                    statutExec: r.imtStatutExec ?? r.ImtStatutExec ?? "",
                    statutResult: r.imtStatutResult ?? r.ImtStatutResult ?? "",
                    code: r.code ?? r.Code ?? "",
                    message: r.message ?? r.Message ?? "",
                    // Vrai texte/type d'erreur du teleservice IMT (a afficher).
                    erreurTexte: r.erreurTexte ?? r.ErreurTexte ?? "",
                    erreurType: r.erreurType ?? r.ErreurType ?? "",
                    nom: r.nom ?? r.Nom ?? "",
                    prenom: r.prenom ?? r.Prenom ?? "",
                    idAdeli: r.idAdeli ?? r.IdAdeli ?? "",
                    idRPPS: r.idRPPS ?? r.IdRPPS ?? "",
                    idPsam: r.idPsam ?? r.IdPsam ?? "",
                };
                commit("setMedecinIMT", med);
                return med;
            } catch (e) {
                console.error("fetchReponseIMT:", e);
                return null;
            }
        },
        // Poll le resultat IMT : l'appel s'execute cote lib sur un thread de fond, on rappelle
        // jusqu'a un statut d'execution terminal (!= EnCoursDExecution) ou epuisement des essais.
        // "EnCoursDExecution" est le seul statut non terminal (cf statutIMTType de la lib).
        async pollReponseIMT({ dispatch, getters }, { tries = 10, delayMs = 700 } = {}) {
            for (let i = 0; i < tries; i++) {
                const med = await dispatch("fetchReponseIMT");
                const exec = (med && med.statutExec) || "";
                if (exec && exec !== "EnCoursDExecution") { return med; }
                await new Promise((r) => setTimeout(r, delayMs));
            }
            return getters.getMedecinIMT;
        },
        async fetchChoixBenec({ commit }) {
            commit("setLoading", true);
            commit("setError", null);
            try {
                const value = await ChoixBenecAPI.getBenec(); // renvoie un nombre ou { benec }
                commit("setChoixBenec", value);
                return value;
            } catch (e) {
                commit("setError", e);
                throw e;
            } finally {
                commit("setLoading", false);
            }
        },

        // Met à jour côté backend (route param) puis met à jour le state
        async updateChoixBenec({ commit }, benec) {
            commit("setLoading", true);
            commit("setError", null);
            try {
                const res = await ChoixBenecAPI.setBenec(benec);
                const value = (res && typeof res.benec !== "undefined") ? res.benec : benec;
                commit("setChoixBenec", value);
                return value;
            } catch (e) {
                commit("setError", e);
                throw e;
            } finally {
                commit("setLoading", false);
            }
        },

        // Variante si tu préfères la querystring
        async updateChoixBenecQuery({ commit }, benec) {
            commit("setLoading", true);
            commit("setError", null);
            try {
                const res = await ChoixBenecAPI.setBenecQuery(benec);
                const value = (res && typeof res.benec !== "undefined") ? res.benec : benec;
                commit("setChoixBenec", value);
                return value;
            } catch (e) {
                commit("setError", e);
                throw e;
            } finally {
                commit("setLoading", false);
            }
        },

        // Si tu veux juste forcer une valeur localement (sans API)
        setChoixBenecLocal({ commit }, benec) {
            commit("setChoixBenec", benec);
        },

        resetError({ commit }) {
            commit("setError", null);
        },
    },
};
